import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export const emptyAifMaster = {
  name: "", legal_name: "", scheme_name: "", manager: "",
  manager_sebi_registration_no: "", aif_sebi_registration_no: "",
  category: "CAT_II", sub_type: "PE", structure: "CLOSED",
  tenure_years: "6", extension_years: "2", vintage_year: "", first_close_date: "",
  final_close_date: "", currency: "INR", target_corpus: "", committed_corpus: "",
  min_commitment: "10000000", accreditation_required: false,
  mgmt_fee_pct: "2", management_fee_basis: "COMMITTED_CAPITAL", hurdle_pct: "8",
  carry_pct: "20", catch_up_pct: "", waterfall: "WHOLE_FUND", setup_fee_pct: "1",
  exit_load: "", distributor_trail_bps: "75", nav_frequency: "QUARTERLY",
  valuation_policy: "", valuation_agency: "", benchmark: "", tax_status: "PASS_THROUGH",
  trustee: "", custodian: "", fund_administrator: "", rta: "", auditor: "",
  depository: "", strategy: "", sectors: "", geographies: "India",
  instrument_types: "", liquidity_terms: "", risk_factors: "", ppm_version: "",
  ppm_date: "", ppm_document_url: "", subscription_status: "OPEN",
};

export function masterFromProduct(product) {
  const source = product || {};
  return Object.fromEntries(
    Object.entries(emptyAifMaster).map(([key, fallback]) => [key, source[key] ?? fallback]),
  );
}

function Field({ label, required, span = "", hint, children }) {
  return (
    <label className={`space-y-1 ${span}`}>
      <span className="text-xs font-medium text-[var(--ink-muted)]">{label}{required ? " *" : ""}</span>
      {children}
      {hint && <span className="block text-[10px] text-[var(--ink-muted)]">{hint}</span>}
    </label>
  );
}

function NativeSelect({ value, onChange, children, testid }) {
  return <select value={value ?? ""} onChange={onChange} data-testid={testid} className="h-10 w-full rounded-md border border-[var(--glass-border)] bg-white px-3 text-sm">{children}</select>;
}

export function AifMasterFields({ value, onChange, idPrefix = "aif-master" }) {
  const set = (key) => (event) => onChange({ ...value, [key]: event.target.value });
  const input = (key, type = "text", props = {}) => (
    <Input type={type} value={value[key] ?? ""} onChange={set(key)} data-testid={`${idPrefix}-${key}`} {...props} />
  );

  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-[var(--brand)]">Identity and registration</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Display name" required span="lg:col-span-2">{input("name")}</Field>
          <Field label="Scheme name">{input("scheme_name")}</Field>
          <Field label="Legal AIF name">{input("legal_name")}</Field>
          <Field label="Investment manager" required>{input("manager")}</Field>
          <Field label="AIF SEBI registration no.">{input("aif_sebi_registration_no")}</Field>
          <Field label="Manager SEBI registration no.">{input("manager_sebi_registration_no")}</Field>
          <Field label="Category" required>
            <NativeSelect value={value.category} onChange={(e) => onChange({ ...value, category: e.target.value, structure: e.target.value === "CAT_III" ? value.structure : "CLOSED", tax_status: e.target.value === "CAT_III" ? "FUND_LEVEL" : "PASS_THROUGH" })} testid={`${idPrefix}-category`}>
              <option value="CAT_I">Category I</option><option value="CAT_II">Category II</option><option value="CAT_III">Category III</option>
            </NativeSelect>
          </Field>
          <Field label="Subcategory" required>
            <NativeSelect value={value.sub_type} onChange={set("sub_type")}>
              <option value="VC">Venture Capital</option><option value="ANGEL">Angel Fund</option>
              <option value="SME">SME Fund</option><option value="SOCIAL">Social Impact</option>
              <option value="INFRA">Infrastructure</option><option value="SPECIAL_SITUATIONS">Special Situations</option>
              <option value="PE">Private Equity</option><option value="PRIVATE_CREDIT">Private Credit</option>
              <option value="REAL_ESTATE">Real Estate</option><option value="LONG_SHORT">Long–Short</option>
              <option value="LONG_ONLY_EQUITY">Long-only Equity</option><option value="LONG_ONLY_DEBT">Long-only Debt</option>
              <option value="MULTI_ASSET">Multi Asset</option><option value="HEDGE">Hedge</option>
            </NativeSelect>
          </Field>
          <Field label="Subscription status">
            <NativeSelect value={value.subscription_status} onChange={set("subscription_status")}>
              <option value="OPEN">Open</option><option value="SOFT_CLOSED">Soft closed</option><option value="CLOSED">Closed</option><option value="WINDING_UP">Winding up</option>
            </NativeSelect>
          </Field>
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold text-[var(--brand)]">Structure and capital</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Structure" required>
            <NativeSelect value={value.category === "CAT_III" ? value.structure : "CLOSED"} onChange={set("structure")}>
              <option value="CLOSED">Close-ended</option>{value.category === "CAT_III" && <option value="OPEN">Open-ended</option>}
            </NativeSelect>
          </Field>
          <Field label="Vintage year">{input("vintage_year", "number")}</Field>
          <Field label="Tenure (years)">{input("tenure_years", "number", { min: "0", step: "1" })}</Field>
          <Field label="Extensions (years)">{input("extension_years", "number", { min: "0", step: "1" })}</Field>
          <Field label="First close">{input("first_close_date", "date")}</Field>
          <Field label="Final close">{input("final_close_date", "date")}</Field>
          <Field label="Currency">{input("currency")}</Field>
          <Field label="Minimum commitment" required>{input("min_commitment", "number", { min: "0" })}</Field>
          <Field label="Target corpus">{input("target_corpus", "number", { min: "0" })}</Field>
          <Field label="Committed corpus">{input("committed_corpus", "number", { min: "0" })}</Field>
          <label className="flex items-center gap-2 pt-6 text-xs text-[var(--ink-muted)]">
            <Checkbox checked={!!value.accreditation_required} onCheckedChange={(checked) => onChange({ ...value, accreditation_required: checked === true })} /> Accredited investor required
          </label>
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold text-[var(--brand)]">Commercial terms</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Management fee %">{input("mgmt_fee_pct", "number", { min: "0", step: "0.01" })}</Field>
          <Field label="Fee basis">
            <NativeSelect value={value.management_fee_basis} onChange={set("management_fee_basis")}>
              <option value="COMMITTED_CAPITAL">Committed capital</option><option value="DRAWN_CAPITAL">Drawn capital</option>
              <option value="NAV">NAV</option><option value="COST">Invested cost</option><option value="OTHER">Other</option>
            </NativeSelect>
          </Field>
          <Field label="Hurdle %">{input("hurdle_pct", "number", { min: "0", step: "0.01" })}</Field>
          <Field label="Carry %">{input("carry_pct", "number", { min: "0", step: "0.01" })}</Field>
          <Field label="Catch-up %">{input("catch_up_pct", "number", { min: "0", step: "0.01" })}</Field>
          <Field label="Waterfall">
            <NativeSelect value={value.waterfall} onChange={set("waterfall")}>
              <option value="WHOLE_FUND">Whole fund / European</option><option value="DEAL_BY_DEAL">Deal-by-deal / American</option><option value="OTHER">Other</option>
            </NativeSelect>
          </Field>
          <Field label="Setup fee %">{input("setup_fee_pct", "number", { min: "0", step: "0.01" })}</Field>
          <Field label="Distributor trail (bps)">{input("distributor_trail_bps", "number", { min: "0", step: "0.01" })}</Field>
          <Field label="Exit load / transfer charges" span="sm:col-span-2">{input("exit_load")}</Field>
          <Field label="Liquidity and redemption terms" span="sm:col-span-2">{input("liquidity_terms")}</Field>
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold text-[var(--brand)]">Operations and valuation</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="NAV / valuation frequency">
            <NativeSelect value={value.nav_frequency} onChange={set("nav_frequency")}>
              <option value="MONTHLY">Monthly</option><option value="QUARTERLY">Quarterly</option>
              <option value="HALF_YEARLY">Half-yearly</option><option value="ANNUAL">Annual</option>
            </NativeSelect>
          </Field>
          <Field label="Tax status">
            <NativeSelect value={value.category === "CAT_III" ? "FUND_LEVEL" : value.tax_status} onChange={set("tax_status")}>
              <option value="PASS_THROUGH">Pass-through</option><option value="FUND_LEVEL">Fund-level</option><option value="MIXED">Mixed / consult documents</option>
            </NativeSelect>
          </Field>
          <Field label="Benchmark">{input("benchmark")}</Field>
          <Field label="Valuation agency">{input("valuation_agency")}</Field>
          <Field label="Trustee">{input("trustee")}</Field><Field label="Custodian">{input("custodian")}</Field>
          <Field label="Fund administrator">{input("fund_administrator")}</Field><Field label="RTA">{input("rta")}</Field>
          <Field label="Auditor">{input("auditor")}</Field><Field label="Depository / ISIN location">{input("depository")}</Field>
          <Field label="Valuation policy" span="sm:col-span-2 lg:col-span-4"><Textarea value={value.valuation_policy ?? ""} onChange={set("valuation_policy")} /></Field>
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold text-[var(--brand)]">Research classification</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Investment strategy" span="sm:col-span-2"><Textarea value={value.strategy ?? ""} onChange={set("strategy")} data-testid={`${idPrefix}-strategy`} /></Field>
          <Field label="Sectors" hint="Comma-separated">{input("sectors")}</Field>
          <Field label="Geographies" hint="Comma-separated">{input("geographies")}</Field>
          <Field label="Instrument types" hint="Equity, debt, convertibles, etc.">{input("instrument_types")}</Field>
          <Field label="Material risk factors"><Textarea value={value.risk_factors ?? ""} onChange={set("risk_factors")} /></Field>
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold text-[var(--brand)]">Placement memorandum evidence</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="PPM version">{input("ppm_version")}</Field>
          <Field label="PPM date">{input("ppm_date", "date")}</Field>
          <Field label="Controlled document URL">{input("ppm_document_url", "url", { placeholder: "https://…" })}</Field>
        </div>
      </section>
    </div>
  );
}
